// Typed client for the Phase 1 conversational-documentation endpoints —
// docs/HMS_V4_API_CONTRACT.md "Conversational document generation" and the
// prescription sheet rows. Streaming generation lives in streamGeneration.ts.
import { api } from '@/lib/api/client';
import type { SourceMarker } from './docAssist';

export interface SourceRef {
  section: string;
  label: string;
  source: { type: string; stage?: string; recorded_by?: string | null; recorded_at?: string | null };
}

export interface Nudge {
  key: string;
  severity: 'warn' | 'info';
  message: string;
  clinicalBasis?: string;
}

export interface ConsultationStart {
  visit_code: string;
  doc_type: string;
  content: string;
  sources: SourceRef[];
  nudges: Nudge[];
}

export interface PrescriptionSheet {
  prescription_id: number;
  generation_source: 'template';
  content: string;
}

export interface SheetComparison {
  applies: boolean;
  matches: boolean;
  missing: string[];
  unexpected: string[];
}

/** "Start from today's captured data" — no AI. */
export function startConsultationDraft(visitCode: string): Promise<ConsultationStart> {
  return api.get(`/consultation/start/${encodeURIComponent(visitCode)}`);
}

/** Deterministic completeness nudges for the document as it currently stands. */
export function checkCompleteness(visitCode: string, content: string): Promise<{ nudges: Nudge[] }> {
  return api.post('/consultation/completeness', { visit_code: visitCode, content });
}

/** The prescription sheet built from the saved rows — no AI. */
export function getPrescriptionSheet(prescriptionId: number): Promise<PrescriptionSheet> {
  return api.get(`/prescriptions/${prescriptionId}/sheet`);
}

/** Compares a sheet's Rx. lines with the saved prescription before approval. */
export function checkPrescriptionSheet(prescriptionId: number, content: string): Promise<SheetComparison> {
  return api.post(`/prescriptions/${prescriptionId}/sheet-check`, { content });
}

const STAGE_LABELS: Record<string, string> = { vitals: 'Vitals stage', pre_consult: 'Pre-consult stage' };
const TYPE_LABELS: Record<string, string> = {
  allergies: 'Allergy record',
  encounter_diagnoses: 'Diagnoses for this visit',
  patient_medications: 'Medication record',
  patient_diagnoses: 'Patient history',
};

/** "Vitals stage · Nurse Priya · 09:20" — the ⓘ tooltip for a pre-filled line. */
export function sourceMarkers(sources: SourceRef[]): SourceMarker[] {
  return sources.map(({ label, source }) => {
    const where = source.stage ? STAGE_LABELS[source.stage] ?? source.stage : TYPE_LABELS[source.type] ?? source.type;
    const when = source.recorded_at
      ? new Date(source.recorded_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
      : null;
    return { label, title: ['From', where, source.recorded_by, when].filter(Boolean).join(' · ').replace('From · ', 'From ') };
  });
}
